import * as THREE from 'three/webgpu';
import { texture, uv, frontFacing, select, vec3, uniform, float } from 'three/tsl';
import { setEnvScale } from './matte';
import { batchByMaterial, batchFlat } from './batch';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

/** Loaded car model with named parts the game animates. */
export interface CarModel {
  body: THREE.Object3D;
  wheels: THREE.Object3D[];
  steeringWheel: THREE.Object3D | null;
  steeringRest: THREE.Quaternion;
  shifter: THREE.Object3D | null;
  shifterRest: THREE.Quaternion;
  handbrake: THREE.Object3D | null;
  handbrakeRest: THREE.Quaternion;
  driver: THREE.Object3D | null;
  display: DashDisplay | null;
  mounts: Record<string, THREE.Vector3>;
  brakeLight: { value: number };
  headLight: { value: number };
  /** Paint material (for dirt build-up, P1). */
  paint: THREE.MeshPhysicalNodeMaterial | null;
  glass: THREE.MeshPhysicalNodeMaterial | null;
}

/** Dashboard digital display drawn to a canvas texture (rpm, shift lights, gear, speed). */
export class DashDisplay {
  readonly canvas: HTMLCanvasElement;
  readonly texture: THREE.CanvasTexture;
  private readonly g: CanvasRenderingContext2D;
  private last = 0;
  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 320;
    this.canvas.height = 112;
    this.g = this.canvas.getContext('2d')!;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    // glTF UVs have v pointing down the image (the loader uses flipY = false for model textures).
    this.texture.flipY = false;
  }

  draw(now: number, rpm: number, limiter: number, gear: number, speedKmh: number, unitsMph: boolean): void {
    if (now - this.last < 60) return;
    this.last = now;
    const g = this.g;
    const W = this.canvas.width;
    const H = this.canvas.height;
    g.fillStyle = '#05070a';
    g.fillRect(0, 0, W, H);
    // Shift lights: 10 LEDs (green → amber → red), flashing at the limiter.
    const frac = Math.max(0, (rpm - 4500) / (limiter - 4500));
    const lit = Math.round(frac * 10);
    const flash = rpm > limiter - 150 && Math.floor(now / 80) % 2 === 0;
    for (let i = 0; i < 10; i++) {
      const on = flash || i < lit;
      g.fillStyle = on ? (i < 4 ? '#2fe26b' : i < 7 ? '#ffb000' : '#ff3030') : '#1a1d22';
      g.fillRect(10 + i * 30, 6, 24, 10);
    }
    // RPM bar.
    g.fillStyle = '#1a1d22';
    g.fillRect(10, 24, W - 20, 12);
    g.fillStyle = rpm > limiter - 400 ? '#ff3030' : '#e8eef2';
    g.fillRect(10, 24, (W - 20) * Math.min(1, rpm / limiter), 12);
    // Gear and speed.
    g.fillStyle = '#ffffff';
    g.font = 'italic 800 64px "Barlow Condensed", "Arial Narrow", sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(gear < 0 ? 'R' : gear === 0 ? 'N' : String(gear), W / 2, 76);
    g.font = '700 30px "Barlow Condensed", "Arial Narrow", sans-serif';
    g.textAlign = 'right';
    const sp = unitsMph ? speedKmh / 1.609 : speedKmh;
    g.fillText(String(Math.round(sp)), W - 14, 76);
    g.textAlign = 'left';
    g.font = '500 22px "Barlow Condensed", "Arial Narrow", sans-serif';
    g.fillText(`${(rpm / 1000).toFixed(1)}k`, 14, 76);
    this.texture.needsUpdate = true;
  }
}

let loader: GLTFLoader | null = null;

export async function loadCarModel(url: string, onProgress?: (f: number) => void): Promise<CarModel> {
  if (!loader) {
    loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
  }
  const gltf = await loader.loadAsync(url, (e) => {
    if (onProgress && e.total) onProgress(e.loaded / e.total);
  });
  const root = gltf.scene;
  const brakeLight = uniform(0);
  const headLight = uniform(0);
  let paint: THREE.MeshPhysicalNodeMaterial | null = null;
  let glass: THREE.MeshPhysicalNodeMaterial | null = null;
  const display = new DashDisplay();
  const converted = new Map<THREE.Material, THREE.Material>();
  const convert = (m: THREE.Material): THREE.Material => {
    const hit = converted.get(m);
    if (hit) return hit;
    const src = m as THREE.MeshStandardMaterial;
    let out: THREE.Material;
    // Blender appends .001 etc. to duplicate datablock names; match on the base name.
    const base = m.name.replace(/\.\d+$/, '');
    switch (base) {
      case 'paint': {
        const p = new THREE.MeshPhysicalNodeMaterial({ roughness: 0.32, metalness: 0.05, clearcoat: 1, clearcoatRoughness: 0.06, side: THREE.DoubleSide });
        const map = src.map;
        if (map) {
          map.colorSpace = THREE.SRGBColorSpace;
          // Seen at grazing angles from the bonnet camera: anisotropic filtering keeps stripes sharp.
          map.anisotropy = 8;
          // Inner shell faces (seen from the cockpit): the shell painted a neutral mid-dark grey, as
          // in most Rally2 cars (near-black read as a navy cave under the blue sky light).
          p.colorNode = select(frontFacing, texture(map, uv()).rgb, vec3(0.085, 0.078, 0.07));
          // ...and no clearcoat inside (it mirrored the sky onto the cabin walls).
          p.clearcoatNode = select(frontFacing, float(1), float(0));
        }
        paint = p;
        out = p;
        break;
      }
      case 'glass': {
        const g = new THREE.MeshPhysicalNodeMaterial({ color: 0x0e1418, roughness: 0.04, metalness: 0, transparent: true, opacity: 0.38, side: THREE.DoubleSide, depthWrite: false });
        setEnvScale(g, 1.4);
        glass = g;
        out = g;
        break;
      }
      case 'light_head': {
        const l = new THREE.MeshStandardNodeMaterial({ color: 0xe8eef0, roughness: 0.1 });
        l.emissiveNode = vec3(1.0, 0.96, 0.88).mul(headLight.mul(6).add(0.15));
        out = l;
        break;
      }
      case 'light_tail': {
        const l = new THREE.MeshStandardNodeMaterial({ color: 0x7a0805, roughness: 0.15 });
        l.emissiveNode = vec3(1.0, 0.04, 0.02).mul(brakeLight.mul(5).add(headLight.mul(0.8)).add(0.22));
        out = l;
        break;
      }
      case 'display': {
        const d = new THREE.MeshBasicNodeMaterial({ map: display.texture });
        d.colorNode = texture(display.texture, uv()).rgb.mul(float(1.6));
        out = d;
        break;
      }
      default: {
        const s = new THREE.MeshStandardNodeMaterial({
          color: src.color ?? new THREE.Color(0x808080),
          roughness: src.roughness ?? 0.6,
          metalness: src.metalness ?? 0,
          map: src.map ?? null,
          side: base === 'satin_black' || base === 'interior' ? THREE.DoubleSide : THREE.FrontSide,
        });
        // The cabin is shaded by the roof: far less sky light than the image-based lighting gives
        // an open surface (it turned the dash and seats navy).
        // Lit only by sky through the glass, so warm the albedo a little to stand in for the light
        // bounced off the ground and the cabin itself.
        if (base === 'interior' || base === 'seat_fabric' || base === 'suit' || base === 'strap' || base === 'book') {
          if (base === 'interior' || base === 'seat_fabric') s.color.multiply(new THREE.Color(1.25, 1.08, 0.88));
        }
        out = s;
      }
    }
    out.name = base;
    converted.set(m, out);
    return out;
  };
  const mounts: Record<string, THREE.Vector3> = {};
  let steeringWheel: THREE.Object3D | null = null;
  let shifter: THREE.Object3D | null = null;
  let handbrake: THREE.Object3D | null = null;
  let driver: THREE.Object3D | null = null;
  let wheelTemplate: THREE.Object3D | null = null;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && !mesh.geometry.attributes.uv && /^display/.test((Array.isArray(mesh.material) ? mesh.material[0] : mesh.material).name)) projectDisplayUVs(mesh.geometry);
    if (mesh.isMesh) {
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(convert) : convert(mesh.material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    }
    switch (o.name) {
      case 'steering_wheel':
        steeringWheel = o;
        break;
      case 'shifter':
        shifter = o;
        break;
      case 'handbrake':
        handbrake = o;
        break;
      case 'driver':
        driver = o;
        break;
      case 'wheel':
        wheelTemplate = o;
        break;
      case 'cockpit_eye':
      case 'bonnet_cam':
      case 'bumper_cam':
      case 'exhaust_tip':
      case 'codriver_seat':
        mounts[o.name] = o.position.clone();
        break;
    }
  });
  // Static parts become one mesh per material (draw calls × shadow cascades dominate CPU time);
  // animated parts stay separate, and only the exterior casts shadows.
  const ANIMATED = new Set(['steering_wheel', 'shifter', 'handbrake', 'driver']);
  const EXTERIOR = new Set(['paint', 'paint_white', 'paint_accent', 'satin_black', 'black_plastic', 'rubber', 'rim', 'metal']);
  const kept = (o: THREE.Object3D) => ANIMATED.has(o.name) || o.name === 'wheel';
  // Flat-coloured parts: one exterior mesh (casts shadows) and one interior mesh; the rest
  // (livery paint, glass, lights, display) one mesh per material.
  batchFlat(root, kept, (m) => EXTERIOR.has(m.name), true, 'exterior');
  const cabin = batchFlat(root, kept, (m) => !EXTERIOR.has(m.name), false, 'interior');
  // The merged cabin material: shaded by the roof, so far less sky than an open surface.
  if (cabin) setEnvScale(cabin.material as THREE.Material, 0.3);
  batchByMaterial(root, kept, (m) => EXTERIOR.has(m.name));
  // Animated parts and the wheel template collapse to one flat mesh each (their roots keep animating).
  for (const name of [...ANIMATED, 'wheel']) {
    const sub = root.getObjectByName(name);
    if (!sub) continue;
    batchFlat(sub, () => false, () => true, name === 'wheel', name);
    batchByMaterial(sub, () => false, () => name === 'wheel');
  }
  const wheels: THREE.Object3D[] = [];
  if (wheelTemplate) {
    const wt = wheelTemplate as THREE.Object3D;
    wt.removeFromParent();
    wt.position.set(0, 0, 0);
    for (let i = 0; i < 4; i++) {
      const w = wt.clone(true);
      // Right-hand wheels (odd indices) are turned 180° about the vertical so the rim faces
      // outward. (Mirroring with scale.x = -1 overwrote the template's own 0.32 scale — the right
      // wheels came out three times as wide — and flipped the triangle winding.)
      if (i % 2 === 1) w.rotateY(Math.PI);
      wheels.push(w);
    }
  }
  const sw = steeringWheel as THREE.Object3D | null;
  const sh = shifter as THREE.Object3D | null;
  const hb = handbrake as THREE.Object3D | null;
  return {
    body: root,
    wheels,
    steeringWheel: sw,
    steeringRest: sw ? sw.quaternion.clone() : new THREE.Quaternion(),
    shifter: sh,
    shifterRest: sh ? sh.quaternion.clone() : new THREE.Quaternion(),
    handbrake: hb,
    handbrakeRest: hb ? hb.quaternion.clone() : new THREE.Quaternion(),
    driver,
    display,
    mounts,
    brakeLight,
    headLight,
    paint,
    glass,
  };
}

/** Planar UVs for the display panel if the exporter dropped them (largest two extents → u, v). */
function projectDisplayUVs(g: THREE.BufferGeometry): void {
  g.computeBoundingBox();
  const b = g.boundingBox!;
  const pos = g.attributes.position;
  const uv = new Float32Array(pos.count * 2);
  const sx = b.max.x - b.min.x || 1;
  const sy = b.max.y - b.min.y || 1;
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = (b.max.x - pos.getX(i)) / sx; // car +X is the driver's left
    uv[i * 2 + 1] = 1 - (pos.getY(i) - b.min.y) / sy;
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}
